import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import farmsConfig from '~/config/constants/farms'
import { FarmChainConfig } from '~/config/constants/types'
import isArchivedPid from '~/utils/farmHelpers'
import priceHelperLpsConfig from '~/config/constants/priceHelperLps'
import fetchFarms from './fetchFarms'
import fetchFarmsPrices from './fetchFarmsPrices'

import {
  fetchFarmUserEarnings,
  fetchFarmUserAllowances,
  fetchFarmUserTokenBalances,
  fetchFarmUserStakedBalances,
  fetchFarmUserDualEarnings,
  fetchZapFarmUserAllowances
} from './fetchFarmUser'
import { SerializedFarmsState, SerializedFarm } from '../types'
import { toSerializeFarmConfig } from './helpers'


const initalUserData = {
  userData: {
    allowance: '0',
    allowanceZap: '0',
    tokenBalance: '0',
    stakedBalance: '0',
    earnings: '0',
  },
}


const noAccountFarmConfig = Object.keys(farmsConfig).reduce( (accum, key) => {
  /* eslint-disable no-param-reassign */
  accum[key] = farmsConfig[key].map(farm => Object.assign(toSerializeFarmConfig( Number.parseInt(key), farm), initalUserData))
  return accum
}, {} )

const initialState: SerializedFarmsState = {
  data: noAccountFarmConfig,
  loadArchivedFarmsData: false,
  userDataLoaded: false,
}

export const nonArchivedFarms : FarmChainConfig = Object.keys(farmsConfig).reduce( (accum, key) => {
  /* eslint-disable no-param-reassign */
  accum[key] = farmsConfig[key].filter(({pid})=> !isArchivedPid(pid))
  return accum
}, {} )


// Async thunks
export const fetchFarmsPublicDataAsync = createAsyncThunk<SerializedFarm[], {chainId: number, pids: number[]}>(
  'farms/fetchFarmsPublicDataAsync',
  async ({ chainId, pids }) => {
    const farmsToFetch = farmsConfig[chainId].filter((farmConfig) => pids.includes(farmConfig.pid)) // add chainId

    // Add price helper farms
    const farmsWithPriceHelpers = farmsToFetch.concat(priceHelperLpsConfig)

    const farms = await fetchFarms(chainId, farmsWithPriceHelpers)
    const farmsWithPrices = await fetchFarmsPrices(chainId, farms)

    // Filter out price helper LP config farms
    const farmsWithoutHelperLps = farmsWithPrices.filter((farm: SerializedFarm) => {
      return farm.pid || farm.pid === 0
    })

    return farmsWithoutHelperLps
  },
)

interface DualEarningResponse {
  rewardToken: string
  rewardAmount: string
}


interface FarmUserDataResponse {
  pid: number
  allowance: string
  allowanceZap: string
  tokenBalance: string
  stakedBalance: string
  earnings: string
  dualEarnings? : DualEarningResponse[]
}


export const fetchFarmUserDataAsync = createAsyncThunk<FarmUserDataResponse[], { chainId: number, account: string; pids: number[] }>(
  'farms/fetchFarmUserDataAsync',
  async ({ chainId, account, pids }) => {

    const farmsToFetch = farmsConfig[chainId].filter((farmConfig) => pids.includes(farmConfig.pid))
    const userFarmAllowances = await fetchFarmUserAllowances(chainId,account, farmsToFetch)
    const userZapFarmAllowances = await fetchZapFarmUserAllowances(chainId, account, farmsToFetch)
    const userFarmTokenBalances = await fetchFarmUserTokenBalances(chainId,account, farmsToFetch)
    const userStakedBalances = await fetchFarmUserStakedBalances(chainId,account, farmsToFetch)
    const userFarmEarnings = await fetchFarmUserEarnings(chainId,account, farmsToFetch)
    const userFarmDualEarnings = await fetchFarmUserDualEarnings(chainId, account, farmsToFetch)

    return userFarmAllowances.map((farmAllowance, index) => {
      return {
        pid: farmsToFetch[index].pid,
        allowance: userFarmAllowances[index],
        allowanceZap: userZapFarmAllowances[index],
        tokenBalance: userFarmTokenBalances[index],
        stakedBalance: userStakedBalances[index],
        earnings: userFarmEarnings[index],
        dualEarnings: userFarmDualEarnings[index]
      }
    })
  },
)

export const farmsSlice = createSlice({
  name: 'Farms',
  initialState,
  reducers: {
    setLoadArchivedFarmsData: (state, action) => {
      const loadArchivedFarmsData = action.payload
      state.loadArchivedFarmsData = loadArchivedFarmsData
    },
  },
  extraReducers: (builder) => {
    // Update farms with live data
    builder.addCase(fetchFarmsPublicDataAsync.fulfilled, (state, action) => {
      state.data[action.meta.arg.chainId] = state.data[action.meta.arg.chainId].map((farm) => {
        const liveFarmData = action.payload.find((farmData) => farmData.pid === farm.pid)
        return { ...farm, ...liveFarmData }
      })
    })

    // Update farms with user data
    builder.addCase(fetchFarmUserDataAsync.fulfilled, (state, action) => {
      action.payload.forEach((userDataEl) => {
        const {chainId} = action.meta.arg
        const { pid } = userDataEl
        const index = state.data[chainId].findIndex((farm) => farm.pid === pid)
        state.data[chainId][index] = { ...state.data[chainId][index], userData: userDataEl }
      })
      state.userDataLoaded = true
    })
  },
})

// Actions
export const { setLoadArchivedFarmsData } = farmsSlice.actions

export default farmsSlice.reducer
